import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import { formatDateWithCulture } from '../../../utils/dateUtils';
import licenseService from '../../../services/licenseService';

function LicenseHistoryModal({
  open,
  onClose,
  license = null,
}) {
  const { t, i18n } = useTranslation();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (open && license?.id) {
      fetchHistory();
    }
  }, [open, license?.id, page, pageSize]);

  const fetchHistory = async () => {
    if (!license?.id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await licenseService.getLicenseHistory(license.id, { page, pageSize });
      setHistoryData(response.data || []);
      setTotal(response.meta?.total || 0);
    } catch (err) {
      setError(err.message || t('licenses.history.fetchError'));
      setHistoryData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setHistoryData([]);
    setPage(1);
    setError(null);
    onClose();
  };

  const columns = [
    {
      key: 'version',
      label: t('licenses.history.version'),
      width: '100px',
    },
    {
      key: 'unitName',
      label: t('licenses.history.unitName'),
      width: '150px',
    },
    {
      key: 'data',
      label: t('licenses.history.data'),
      render: (value) => value || '-',
    },
    {
      key: 'ipAddress',
      label: t('licenses.history.ipAddress'),
      width: '120px',
      render: (value) => value || '-',
    },
    {
      key: 'userAgent',
      label: t('licenses.history.userAgent'),
      width: '200px',
      render: (value) => value ? (value.length > 50 ? value.substring(0, 50) + '...' : value) : '-',
    },
    {
      key: 'createdOn',
      label: t('licenses.history.createdOn'),
      width: '150px',
      render: (value) => formatDateWithCulture(value, i18n.language) || '-',
    },
    {
      key: 'usageFrequency',
      label: t('licenses.history.usageFrequency'),
      width: '120px',
      render: (value) => value ?? 0,
    },
  ];

  if (!license) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'secondary.main',
        }}
      >
        <HistoryIcon />
        {t('licenses.history.title')}
      </DialogTitle>
      <DialogContent>
        {/* License Info */}
        <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>{t('licenses.history.licenseCode')}:</strong> {license.code}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>{t('licenses.history.currentVersion')}:</strong> {license.version || t('licenses.history.noVersion')}
          </Typography>
          {license.unitName && (
            <Typography variant="body2">
              <strong>{t('licenses.history.assignedUnit')}:</strong> {license.unitName}
            </Typography>
          )}
        </Box>

        {/* History Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'error.main' }}>
            <Typography>{error}</Typography>
          </Box>
        ) : historyData.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            <Typography>{t('licenses.history.noHistory')}</Typography>
          </Box>
        ) : (
          <Table
            id="license-history-table"
            columns={columns}
            data={historyData}
            selectable={false}
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            pageSizeOptions={[5, 10, 20]}
            emptyMessage={t('licenses.history.noHistory')}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          startIcon={<CloseIcon />}
          id="closeLicenseHistory"
          name="closeLicenseHistory"
          data-testid="license-history-modal-button-close"
        >
          {t('licenses.history.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LicenseHistoryModal;
